import React, { ReactNode, useMemo, useState } from 'react';

// 勾选清单: 搜索框 + 全选 (作用于当前筛选结果) + 可滚动勾选行.
// 导出前挑选数据的通用件 (材料主数据导出 / 工艺参数导出共用), 选中集由父组件持有.

interface Props<T> {
    items: T[];
    /** 行唯一键, 也是 selected 集合里存的值 */
    keyOf: (item: T) => string;
    /** 行内容 (勾选框右侧) */
    render: (item: T) => ReactNode;
    /** 参与搜索匹配的文本, 组件内统一转小写比较 */
    searchText: (item: T) => string;
    selected: Set<string>;
    onChange: (next: Set<string>) => void;
    loading?: boolean;
    searchPlaceholder?: string;
    emptyText?: string;
    /** 列表最大高度 (tailwind class), 默认 max-h-80 */
    maxHeightClass?: string;
}

function CheckPickList<T>({
    items,
    keyOf,
    render,
    searchText,
    selected,
    onChange,
    loading,
    searchPlaceholder = '搜索',
    emptyText = '暂无数据',
    maxHeightClass = 'max-h-80',
}: Props<T>) {
    const [search, setSearch] = useState('');

    const filtered = useMemo(() => {
        const k = search.trim().toLowerCase();
        if (!k) return items;
        return items.filter((it) => searchText(it).toLowerCase().includes(k));
        // searchText 由调用方以内联箭头函数传入, 不进依赖数组避免每次渲染重算
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, search]);

    // 全选语义: 勾/取消当前筛选结果的全部行 (无搜索词时即全部行)
    const allFilteredChecked = filtered.length > 0 && filtered.every((it) => selected.has(keyOf(it)));

    const toggleAll = () => {
        const next = new Set(selected);
        if (allFilteredChecked) filtered.forEach((it) => next.delete(keyOf(it)));
        else filtered.forEach((it) => next.add(keyOf(it)));
        onChange(next);
    };

    const toggleOne = (key: string) => {
        const next = new Set(selected);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        onChange(next);
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-3">
                <input
                    className="inp flex-1"
                    placeholder={searchPlaceholder}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <label className="inline-flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer select-none whitespace-nowrap">
                    <input
                        type="checkbox"
                        checked={allFilteredChecked}
                        onChange={toggleAll}
                        disabled={loading || filtered.length === 0}
                    />
                    全选{search.trim() ? ' (筛选结果)' : ''}
                </label>
            </div>
            <div className={`border border-slate-200 rounded overflow-auto ${maxHeightClass}`}>
                {loading && <div className="p-4 text-center text-sm text-slate-400">加载中…</div>}
                {!loading && filtered.length === 0 && (
                    <div className="p-4 text-center text-sm text-slate-400">{emptyText}</div>
                )}
                {!loading &&
                    filtered.map((it) => {
                        const key = keyOf(it);
                        return (
                            <label
                                key={key}
                                className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-b-0"
                            >
                                <input
                                    type="checkbox"
                                    checked={selected.has(key)}
                                    onChange={() => toggleOne(key)}
                                />
                                {render(it)}
                            </label>
                        );
                    })}
            </div>
        </div>
    );
}

export default CheckPickList;
